import { useState, useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";
import { Container, Row, Col, Button, Form, Alert, Spinner, Modal } from "react-bootstrap";
import { toast } from 'react-toastify';

import api from "../../services/api";
import Template from "../../template";
import { useAuth } from "../../context/auth";
import CarregarPDFUERJ from "../../components/CarregarPDFUERJ";

import * as S from "./styled";

function CorrecaoUERJ(props) {
  const { id } = props.match.params;
  const history = useHistory();
  const { user } = useAuth();

  // Estados para dados da redação
  const [redacao, setRedacao] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [redacaoInfo, setRedacaoInfo] = useState({ aluno: '', turma: '', tema: '' });
  // Demais redações do mesmo aluno, para alternar entre elas sem sair da tela
  const [redacoesDoAluno, setRedacoesDoAluno] = useState([]);

  /*
    Dono da correção, como na tela do ENEM: quem marcou primeiro fica com o
    crédito, e quem chega depois precisa saber disso antes de mexer.
  */
  const [dono, setDono] = useState(null);
  const [souODono, setSouODono] = useState(true);
  const [confirmandoEdicao, setConfirmandoEdicao] = useState(false);
  const [avisoFechado, setAvisoFechado] = useState(false);
  const [buscaOutraRedacao, setBuscaOutraRedacao] = useState("");

  /*
    Competências começam em null (= "não preenchida"), não em 0.
    Antes começavam em 0 e a validação de "preencha todas as competências"
    nunca disparava (0 == null é falso), então dava pra finalizar a correção
    sem ter escolhido nota nenhuma e ela era gravada como 0 — indistinguível
    de um zero legítimo. Nota 0,0 continua sendo uma nota válida; o que não
    pode é "nunca escolhida" virar 0 silenciosamente.
  */
  const [adequacaoTema, setAdequacaoTema] = useState(null);
  const [tipoTexto, setTipoTexto] = useState(null);
  const [argumentacao, setArgumentacao] = useState(null);
  const [periodoCoesao, setPeriodoCoesao] = useState(null);
  const [modalidade, setModalidade] = useState(null);
  // Subcompetências selecionadas (códigos 11..15, 21..25, ... 51..55)
  const [subAdequacaoTema, setSubAdequacaoTema] = useState([]);
  const [subTipoTexto, setSubTipoTexto] = useState([]);
  const [subArgumentacao, setSubArgumentacao] = useState([]);
  const [subPeriodoCoesao, setSubPeriodoCoesao] = useState([]);
  const [subModalidade, setSubModalidade] = useState([]);

  // Estados para marcações visuais
  const [marcacoes, setMarcacoes] = useState([]);
  const [competenciaAtiva, setCompetenciaAtiva] = useState("adequacao_tema");
  const pdfRef = useRef(null);

  // Mapa de cores por competência (mesmo padrão do PDF)
  const competenciaColors = {
    adequacao_tema: "#ff6b6b",
    tipo_texto: "#4ecdc4",
    argumentacao: "#45b7d1",
    periodo_coesao: "#96ceb4",
    modalidade: "#feca57"
  };

  const hexToRgba = (hex, alpha = 0.15) => {
    const cleaned = hex.replace('#', '');
    const bigint = parseInt(cleaned, 16);
    const r = (bigint >> 16) & 255;
    const g = (bigint >> 8) & 255;
    const b = bigint & 255;
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  const getCompetenciaColor = (comp) => competenciaColors[comp] || "#6c757d";

  // Suporte a códigos de competência e números
  const CODE_TO_COMPETENCIA = {
    a: "adequacao_tema",
    t: "tipo_texto",
    r: "argumentacao",
    p: "periodo_coesao",
    m: "modalidade"
  };

  const NUMBER_TO_COMPETENCIA = {
    "1": "modalidade",
    "2": "tipo_texto",
    "3": "argumentacao",
    "4": "periodo_coesao",
    "5": "adequacao_tema"
  };

  const normalizeCompetencia = (comp) => {
    if (comp === undefined || comp === null) return null;
    const val = String(comp).toLowerCase().trim();
    if (competenciaColors[val]) return val; // já é chave completa
    if (CODE_TO_COMPETENCIA[val]) return CODE_TO_COMPETENCIA[val]; // letra
    if (NUMBER_TO_COMPETENCIA[val]) return NUMBER_TO_COMPETENCIA[val]; // número
    return val; // fallback
  };

  const resolveCompetencia = (marcacao) => {
    const raw = marcacao?.competencia ?? marcacao?.content?.competencia ?? marcacao?.conteudo?.competencia ?? marcacao?.comment?.competencia;
    return normalizeCompetencia(raw);
  };

  const competenciaLabels = {
    adequacao_tema: 'Adequação ao Tema',
    tipo_texto: 'Tipo de Texto',
    argumentacao: 'Argumentação',
    periodo_coesao: 'Período/Coesão',
    modalidade: 'Modalidade'
  };

  const getCompetenciaLabel = (compKey) => competenciaLabels[compKey] || 'Competência';

  // Mapeamento de subcompetências (sem pontuações)
  const SUBCOMPETENCIAS = {
    adequacao_tema: [
      { code: 11, label: 'Não trata o tema' },
      { code: 12, label: 'Tangencia o tema' },
      { code: 13, label: 'Trata o tema de forma previsível' },
      { code: 14, label: 'Possui marcas pontuais de autoria e argumentos previsíveis' },
      { code: 15, label: 'Autoria e Defesa Consistente de Ponto de Vista' },
    ],
    tipo_texto: [
      { code: 21, label: 'Não é de modo algum uma dissertação. Não é texto em prosa e/ou não defende uma opinião.' },
      { code: 22, label: 'É antes uma dissertação ou narração com difuso propósito dissertativo.' },
      { code: 23, label: 'Enumera fatos ou ideias sem feição dissertativa.' },
      { code: 24, label: 'Atende à estrutura da dissertação, MAS DE MANEIRA ESQUEMÁTICA, COMO SE PRESO A UM MODELO PRÉVIO.' },
      { code: 25, label: 'Atende à estrutura da dissertação de maneira claramente pessoal e consistente.' },
    ],
    argumentacao: [
      { code: 31, label: 'Não desenvolve a argumentação' },
      { code: 32, label: 'Argumentos com pouca pertinência ou suficiência' },
      { code: 33, label: 'Argumenta, ainda que de modo pertinente, mas sem projeto claro de texto.' },
      { code: 34, label: 'Apresenta argumentos pertinentes, mas com incoerências localizadas ou com pouca eficácia.' },
      { code: 35, label: 'Argumenta de maneira pertinente, suficiente e coerente.' },
    ],
    periodo_coesao: [
      { code: 41, label: 'Estruturação caótica (impede a clareza)' },
      { code: 42, label: 'Problemas frequentes de estruturação sintática e/ou de uso precário de elementos coesivos (afetam clareza)' },
      { code: 43, label: 'Problemas localizados de estruturação de período e/ou de coesão' },
      { code: 44, label: 'Uso adequado de recursos coesivos e estruturação sintática sem comprometimento' },
      { code: 45, label: 'Uso produtivo de recursos coesivos e de recursos sintáticos (ênfase, delimitação, paralelismo, etc.)' },
    ],
    modalidade: [
      { code: 51, label: 'Problemas graves e generalizados de modalidade' },
      { code: 52, label: 'Problemas de modalidade pouco aceitáveis para o nível de escolaridade' },
      { code: 53, label: 'Problemas localizados e/ou marcas de oralidade' },
      { code: 54, label: 'Bom domínio da variedade padrão da língua' },
      { code: 55, label: 'Excelente domínio da variedade da língua' },
    ],
  };

  // Opções de pontuação manual para cada competência (0.0 até 4.0 em passos de 0.1)
  const SCORE_OPTIONS = Array.from({ length: 41 }, (_, i) => (i / 10).toFixed(1));

  /* null/undefined/NaN => null ("não preenchida"); qualquer número válido passa. */
  const numeroOuNulo = (valor) => {
    if (valor === null || valor === undefined || valor === "") return null;
    const n = Number(valor);
    return Number.isFinite(n) ? n : null;
  };

  /* Valor do <select>: "" quando não preenchida, para casar com a opção placeholder. */
  const valorSelect = (nota) => (nota === null ? "" : nota.toFixed(1));

  /* Texto do rótulo: mostra que está em aberto em vez de fingir um "0,0". */
  const rotuloNota = (nota) =>
    nota === null ? "não preenchida" : `${nota.toFixed(1).replace(".", ",")}/4,0`;

  // Removido cálculo automático de pontuação por subcompetências.

  // Buscar dados da redação específica
  useEffect(() => {
    async function buscarRedacao() {
      try {
        setLoading(true);

        /*
          Com o seletor lateral o componente não é mais desmontado entre uma redação
          e outra — só o `id` muda. Sem zerar aqui, nota, subcompetências e marcações
          da redação anterior continuariam na tela e poderiam ser salvas na nova.
        */
        setRedacao(null);
        setError("");
        setAdequacaoTema(null);
        setTipoTexto(null);
        setArgumentacao(null);
        setPeriodoCoesao(null);
        setModalidade(null);
        setSubAdequacaoTema([]);
        setSubTipoTexto([]);
        setSubArgumentacao([]);
        setSubPeriodoCoesao([]);
        setSubModalidade([]);
        setMarcacoes([]);
        setBuscaOutraRedacao("");

        console.log("🔍 Buscando redação UERJ com ID:", id);
        const response = await api.get(`/redacao/uerj/${id}`);
        console.log("📊 Resposta da API redação UERJ:", response.data);
        
        if (response.data.redacao) {
          const redacaoData = response.data.redacao;
          console.log("✅ Dados da redação carregados:", redacaoData);
          setRedacao(redacaoData);
          setDono(response.data.dono || null);
          setSouODono(response.data.souODono !== false);

          /*
            Busca as redações do próprio aluno em vez da listagem completa da prova.
            Serve para dois fins: preencher aluno/turma/tema (que era o único uso antes)
            e alimentar o seletor lateral, que deixa o professor pular de uma redação
            do aluno para outra sem voltar e refazer os filtros.
          */
          try {
            const listaResponse = await api.get(`/redacoes-aluno/uerj/${redacaoData.user_id}`);
            const redacoes = listaResponse.data.redacoes || [];
            setRedacoesDoAluno(redacoes);

            const found = redacoes.find(r => String(r.id_redacao) === String(id));
            setRedacaoInfo({
              aluno: found?.aluno_nome || '',
              turma: found?.classe_nome || redacaoData.classe_nome || '',
              tema: found?.tema_nome || redacaoData.tema_nome || '',
            });
          } catch (e) {
            console.error("Erro ao buscar as demais redações do aluno:", e);
            setRedacoesDoAluno([]);
            setRedacaoInfo({
              aluno: '',
              turma: redacaoData.classe_nome || '',
              tema: redacaoData.tema_nome || '',
            });
          }

          /*
            `??` e não `||`: com `||` uma nota 0 legítima virava 0 de novo por
            acaso, e o que era NULL no banco (nunca corrigida) também virava 0,
            fazendo a tela mostrar "0,0" como se fosse nota dada.
          */
          setAdequacaoTema(numeroOuNulo(redacaoData.adequacao_tema));
          setTipoTexto(numeroOuNulo(redacaoData.tipo_texto));
          setArgumentacao(numeroOuNulo(redacaoData.argumentacao));
          setPeriodoCoesao(numeroOuNulo(redacaoData.periodo_coesao));
          setModalidade(numeroOuNulo(redacaoData.modalidade));

          // Preencher subcompetências se existirem
          setSubAdequacaoTema(Array.isArray(redacaoData.sub_adequacao_tema) ? redacaoData.sub_adequacao_tema : []);
          setSubTipoTexto(Array.isArray(redacaoData.sub_tipo_texto) ? redacaoData.sub_tipo_texto : []);
          setSubArgumentacao(Array.isArray(redacaoData.sub_argumentacao) ? redacaoData.sub_argumentacao : []);
          setSubPeriodoCoesao(Array.isArray(redacaoData.sub_periodo_coesao) ? redacaoData.sub_periodo_coesao : []);
          setSubModalidade(Array.isArray(redacaoData.sub_modalidade) ? redacaoData.sub_modalidade : []);
        } else {
          console.log("❌ Nenhuma redação encontrada na resposta");
        }
      } catch (err) {
        console.error("❌ Erro ao buscar redação:", err);
        setError("Erro ao carregar a redação. Verifique se você tem permissão para acessá-la.");
      } finally {
        setLoading(false);
      }
    }

    if (id) {
      buscarRedacao();
    }
  }, [id]);

  // Removido o recálculo automático: as subcompetências não alteram mais a pontuação.

  // Buscar marcações existentes
  useEffect(() => {
    async function buscarMarcacoes() {
      try {
        const response = await api.get(`/marcacao/${id}`);
        if (response.data.data) {
          // Marcar todas as marcações carregadas do backend como salvas
          const marcacoesSalvas = response.data.data.map(marcacao => {
            // Parse do conteúdo se for string JSON
            let parsedContent = marcacao.conteudo;
            if (typeof marcacao.conteudo === 'string') {
              try {
                parsedContent = JSON.parse(marcacao.conteudo);
              } catch (e) {
                console.error('Erro ao fazer parse do conteúdo:', e);
                parsedContent = marcacao.conteudo;
              }
            }
            
            return {
              ...marcacao,
              id: marcacao.id_marcacao ?? marcacao.id ?? `marcacao_${Date.now()}_${Math.random().toString(36).substr(2,5)}`,
              content: parsedContent, // Garantir que content está disponível
              saved: true, // Marcações vindas do backend já estão salvas
              deleted: marcacao.deleted ?? false
            };
          });
          console.log('Marcações carregadas:', marcacoesSalvas); // Debug
          setMarcacoes(marcacoesSalvas);
        } else {
          /*
            Redação sem marcação precisa zerar a lista. Antes o componente era
            desmontado ao trocar de redação e isso não importava; com o seletor
            lateral, sem este else as marcações da redação anterior ficariam.
          */
          setMarcacoes([]);
        }
      } catch (err) {
        console.error("Erro ao buscar marcações:", err);
        setMarcacoes([]);
      }
    }

    if (id) {
      buscarMarcacoes();
    }
  }, [id]);

  // Função para salvar correção
  const salvarCorrecao = async () => {
    try {
      setSaving(true);

      /*
        Bloqueia o envio enquanto faltar competência e diz QUAIS faltam. Antes essa
        checagem era letra morta (os estados nasciam em 0) e a correção era gravada
        com 0 sem ninguém perceber.
      */
      const faltando = [
        [adequacaoTema, "Adequação ao Tema"],
        [tipoTexto, "Tipo de Texto"],
        [argumentacao, "Argumentação"],
        [periodoCoesao, "Período e Coesão"],
        [modalidade, "Modalidade"],
      ]
        .filter(([nota]) => !Number.isFinite(nota))
        .map(([, rotulo]) => rotulo);

      if (faltando.length > 0) {
        toast.error(
          `Preencha a pontuação de: ${faltando.join(", ")}. A correção não foi salva.`,
          { autoClose: 8000 }
        );
        return;
      }

      // soma em 0–20 com decimais
      const somaBruta =
        adequacaoTema + tipoTexto + argumentacao + periodoCoesao + modalidade;

      const notaArredondada = Math.round(somaBruta);

      const dadosCorrecao = {
        id_redacao: parseInt(id),
        adequacao_tema: adequacaoTema,
        sub_adequacao_tema: subAdequacaoTema,
        tipo_texto: tipoTexto,
        sub_tipo_texto: subTipoTexto,
        argumentacao: argumentacao,
        sub_argumentacao: subArgumentacao,
        periodo_coesao: periodoCoesao,
        sub_periodo_coesao: subPeriodoCoesao,
        modalidade: modalidade,
        sub_modalidade: subModalidade,
        nota_final: notaArredondada,   // 0–20 (inteiro)
        nota_total: notaArredondada   // manda com os dois nomes pra garantir compatibilidade
      };

      // Salvar correção usando o endpoint PATCH /correcao/uerj
      const response = await api.patch("/correcao/uerj", dadosCorrecao);

      /*
        O interceptor do axios resolve com `false` em 401/403/404 em vez de rejeitar,
        então `response` pode não ser uma resposta. E antes, se a mensagem não batesse,
        nada acontecia: nem toast, nem erro — o corretor achava que tinha salvo.
      */
      if (response?.data?.message === "Redação corrigida com sucesso") {
        toast.success("Correção salva com sucesso!");

        // Redirecionar de volta para a lista de redações
        setTimeout(() => {
          voltarParaLista();
        }, 1500);
      } else {
        console.error("Resposta inesperada ao salvar correção:", response);
        toast.error(
          "Não foi possível confirmar o salvamento. Confira a nota na lista antes de sair desta tela.",
          { autoClose: 10000 }
        );
      }

    } catch (err) {
      console.error("Erro ao salvar correção:", err);
      if (err.response?.status === 400) {
        // O backend recusa correção sem nota e devolve quais competências faltaram
        const problemas = err.response?.data?.problemas;
        toast.error(
          Array.isArray(problemas) && problemas.length > 0
            ? `A correção não foi salva. ${problemas.join("; ")}.`
            : err.response?.data?.message || "A correção não foi salva: dados inválidos.",
          { autoClose: 10000 }
        );
      } else if (err.response?.status === 403) {
        toast.error("Você não tem permissão para corrigir esta redação.");
      } else if (err.response?.status === 404) {
        toast.error("Redação não encontrada.");
      } else if (err.response?.status === 409) {
        toast.error("Esta redação já foi corrigida e não pode ser corrigida novamente.");
      } else {
        toast.error("Erro ao salvar a correção. Tente novamente.");
      }
    } finally {
      setSaving(false);
    }
  };

  // Função para salvar marcação visual
  const salvarMarcacao = async (marcacaoData) => {
    try {
      const response = await api.post("/correcao/marcacao", {
        redacao_id: parseInt(id),
        conteudo: JSON.stringify(marcacaoData)
      });

      if (response.data.message === "Correção salva com sucesso.") {
        toast.success("Marcação salva com sucesso!");
        // Recarregar marcações
        const marcacoesResponse = await api.get(`/marcacao/${id}`);
        if (marcacoesResponse.data.data) {
          // Marcar todas as marcações carregadas do backend como salvas
          const marcacoesSalvas = marcacoesResponse.data.data.map(marcacao => {
            // Parse do conteúdo se for string JSON
            let parsedContent = marcacao.conteudo;
            if (typeof marcacao.conteudo === 'string') {
              try {
                parsedContent = JSON.parse(marcacao.conteudo);
              } catch (e) {
                console.error('Erro ao fazer parse do conteúdo:', e);
                parsedContent = marcacao.conteudo;
              }
            }
            
            return {
              ...marcacao,
              content: parsedContent, // Garantir que content está disponível
              saved: true // Marcações vindas do backend já estão salvas
            };
          });
          setMarcacoes(marcacoesSalvas);
        }
      }
    } catch (err) {
      console.error("Erro ao salvar marcação:", err);
      toast.error("Erro ao salvar marcação.");
    }
  };

  /*
    Volta para a listagem preservando os filtros que o professor tinha montado.

    A origem chega pelo state do router ou pela query `?voltar=`. A query existe
    porque o state não sobrevive a um F5 nem a uma recarga de página; sem ela, o
    corretor que atualizasse a tela caía na listagem sem filtro.
  */
  const voltarParaLista = () => {
    const origem =
      history.location.state?.voltarPara ||
      new URLSearchParams(history.location.search).get("voltar") ||
      "/redacoes-uerj";

    history.push(origem);
  };

  // As demais redações do aluno, sem repetir a que já está aberta
  const outrasRedacoesDoAluno = redacoesDoAluno.filter(
    (r) => String(r.id_redacao) !== String(id)
  );

  /*
    Aluno com muitas redações deixaria o painel lateral gigante: acima de 4 itens
    a lista rola dentro do próprio bloco e aparece um campo de busca por tema.
  */
  const LIMITE_SEM_SCROLL = 4;
  const listaComprida = outrasRedacoesDoAluno.length > LIMITE_SEM_SCROLL;

  const termoBusca = buscaOutraRedacao.trim().toLowerCase();
  const outrasRedacoesFiltradas = termoBusca
    ? outrasRedacoesDoAluno.filter((r) =>
        (r.tema_nome || "").toLowerCase().includes(termoBusca)
      )
    : outrasRedacoesDoAluno;

  const trocarDeRedacao = (outroId) => {
    /*
      Marcação só existe no banco depois de "Salvar Marcações"; trocar de redação
      descarta as que ainda estão pendentes, então confirma antes de perder.
    */
    const pendentes = marcacoes.filter((m) => !m.saved).length;

    if (pendentes > 0) {
      const seguir = window.confirm(
        `Você tem ${pendentes} marcação(ões) ainda não salva(s) nesta redação. ` +
        `Se trocar agora, elas serão perdidas. Deseja continuar?`
      );
      if (!seguir) return;
    }

    /*
      `replace` e não `push`: pular entre cinco redações do aluno não pode empilhar
      cinco entradas no histórico, senão o "Voltar" teria que ser clicado cinco vezes
      para chegar na listagem. O `voltarPara` é repassado para não se perder na troca.
    */
    history.replace(`/correcao-uerj/${outroId}`, {
      voltarPara: history.location.state?.voltarPara,
    });
  };

  // Nota total exibida (derivada das competências) - inteiro 0..20
  const notas = [adequacaoTema, tipoTexto, argumentacao, periodoCoesao, modalidade];
  const competenciasFaltando = notas.filter((n) => !Number.isFinite(n)).length;

  const somaBrutaDisplay = notas.reduce((total, n) => total + (Number.isFinite(n) ? n : 0), 0);

  const notaArredondada = Math.round(somaBrutaDisplay);

  if (loading) {
    return (
      <Template initialSidebarOpen={user?.nivel !== 'e' ? false : undefined}>
        <Container>
          <div className="text-center p-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-3">Carregando redação...</p>
          </div>
        </Container>
      </Template>
    );
  }

  if (error) {
    return (
      <Template initialSidebarOpen={user?.nivel !== 'e' ? false : undefined}>
        <Container>
          <Alert variant="danger" className="mt-4">
            <Alert.Heading>Erro ao carregar redação</Alert.Heading>
            <p>{error}</p>
            <Button variant="outline-danger" onClick={() => voltarParaLista()}>
              Voltar para Lista de Redações
            </Button>
          </Alert>
        </Container>
      </Template>
    );
  }

  if (!redacao) {
    return (
      <Template initialSidebarOpen={user?.nivel !== 'e' ? false : undefined}>
        <Container>
          <Alert variant="warning" className="mt-4">
            <Alert.Heading>Redação não encontrada</Alert.Heading>
            <p>A redação solicitada não foi encontrada.</p>
            <Button variant="outline-warning" onClick={() => voltarParaLista()}>
              Voltar para Lista de Redações
            </Button>
          </Alert>
        </Container>
      </Template>
    );
  }

  return (
    <Template initialSidebarOpen={user?.nivel !== 'e' ? false : undefined}>
      <S.MainWrapper>
        <Container fluid>
          <Row>
            <Col sm={12}>
              <S.MainContent>

                {/*
                  Aviso de que a correção já tem dono, igual ao do fluxo ENEM.
                  Aqui pesa ainda mais: no UERJ a correção não pode ser refeita
                  depois de finalizada, então quem assume está decidindo por todos.
                */}
                {dono && !souODono && !avisoFechado && (
                  <Alert
                    variant="warning"
                    className="d-flex align-items-center"
                    style={{ borderLeft: "6px solid #f39200" }}
                  >
                    <i className="fas fa-triangle-exclamation mr-2" />
                    <div style={{ flex: 1, fontSize: "14px" }}>
                      <strong>Esta correção já tem dono: {dono.nome}.</strong>{" "}
                      {dono.finalizada
                        ? "Ela já foi finalizada."
                        : "Ela foi iniciada e ainda não foi finalizada."}{" "}
                      Você pode marcar e pontuar, mas o crédito continua sendo dele — o que
                      você fizer fica registrado como revisão.
                    </div>
                    <button
                      type="button"
                      className="btn btn-sm btn-link text-dark"
                      onClick={() => setAvisoFechado(true)}
                      title="Fechar aviso"
                    >
                      ×
                    </button>
                  </Alert>
                )}

                {/* Layout em colunas: card de competências à esquerda, redação à direita */}
                <Row className="full-height-row">
                  {/* Card de Competências UERJ (lateral esquerda) */}
                  <Col xs={12} md={2} lg={2}>
                    <div className="card sidebar-card border-0 shadow-sm">
                      <div className="card-header bg-primary text-white d-flex justify-content-between align-items-center">
                        <h5 className="mb-0">
                          <i className="fas fa-clipboard-check mr-2"></i>
                          Competências UERJ
                        </h5>
                        <Button 
                          variant="outline-light" 
                          size="sm"
                          onClick={() => voltarParaLista()}
                        >
                          <i className="fas fa-arrow-left mr-1"></i>
                          Voltar
                        </Button>
                      </div>
                        <div className="card-body">
                          {/* Ações */}
                          <div className="d-grid gap-2 mb-3">
                            <Button
                              variant="success"
                              size="lg"
                              onClick={() => {
                                /* correção de outro: confirma antes de gravar */
                                if (dono && !souODono) {
                                  setConfirmandoEdicao(true);
                                  return;
                                }

                                salvarCorrecao();
                              }}
                              disabled={saving}
                            >
                              {saving ? (
                                <>
                                  <Spinner animation="border" size="sm" className="mr-2" />
                                  Salvando...
                                </>
                              ) : (
                                <>
                                  <i className="fas fa-save mr-2"></i>
                                  Finalizar Correção
                                </>
                              )}
                            </Button>
                            
                            <Button
                              variant="outline-secondary"
                              onClick={() => voltarParaLista()}
                            >
                              <i className="fas fa-times mr-1"></i>
                              Cancelar
                            </Button>
                          </div>
                          {(redacaoInfo.aluno || redacaoInfo.turma || redacaoInfo.tema) && (
                            <div style={{
                              backgroundColor: '#e8f4fd',
                              border: '1px solid #bee5fd',
                              borderRadius: '8px',
                              padding: '10px 12px',
                              marginTop: '12px',
                              marginBottom: '4px',
                              fontSize: '13px',
                              lineHeight: 1.6
                            }}>
                              {redacaoInfo.aluno && (
                                <div><strong>👤 Aluno:</strong> {redacaoInfo.aluno}</div>
                              )}
                              {redacaoInfo.turma && (
                                <div><strong>🏫 Turma:</strong> {redacaoInfo.turma}</div>
                              )}
                              {redacaoInfo.tema && (
                                <div><strong>📝 Tema:</strong> {redacaoInfo.tema}</div>
                              )}
                            </div>
                          )}

                          {/*
                            Seletor das outras redações do mesmo aluno: na monitoria o
                            professor precisa pular entre elas, e antes só dava voltando
                            para a listagem e refazendo todos os filtros.
                          */}
                          {outrasRedacoesDoAluno.length > 0 && (
                            <div className="border rounded p-2 mt-3 mb-2" style={{ fontSize: '12px' }}>
                              <div className="font-weight-bold text-dark mb-2" style={{ fontSize: '13px' }}>
                                <i className="fas fa-layer-group text-primary mr-1"></i>
                                Outras redações deste aluno ({outrasRedacoesDoAluno.length})
                              </div>

                              {listaComprida && (
                                <input
                                  type="search"
                                  className="form-control form-control-sm mb-2"
                                  style={{ fontSize: '12px' }}
                                  placeholder="Buscar por tema..."
                                  value={buscaOutraRedacao}
                                  onChange={(e) => setBuscaOutraRedacao(e.target.value)}
                                  aria-label="Buscar entre as outras redações deste aluno"
                                />
                              )}

                              {/* acima de 4 itens a lista rola aqui dentro em vez de esticar o painel */}
                              <div style={listaComprida ? { maxHeight: '260px', overflowY: 'auto' } : undefined}>
                                {outrasRedacoesFiltradas.map((r) => (
                                  <button
                                    key={r.id_redacao}
                                    type="button"
                                    className="btn btn-link text-left p-2 mb-1 w-100 border rounded"
                                    style={{ fontSize: '12px', lineHeight: 1.35, textDecoration: 'none', whiteSpace: 'normal' }}
                                    onClick={() => trocarDeRedacao(r.id_redacao)}
                                    title={r.tema_nome}
                                  >
                                    <span className="d-block text-dark">{r.tema_nome || 'Tema não disponível'}</span>
                                    <span className="d-block mt-1">
                                      <span className={`badge ${r.status === 'CORRIGIDA' ? 'badge-success' : 'badge-warning'}`}>
                                        {r.status === 'CORRIGIDA' ? 'Corrigida' : 'Aguardando'}
                                      </span>
                                      {r.enviada_date && (
                                        <span className="text-muted ml-2">
                                          {new Date(r.enviada_date).toLocaleDateString('pt-BR')}
                                        </span>
                                      )}
                                    </span>
                                  </button>
                                ))}

                                {outrasRedacoesFiltradas.length === 0 && (
                                  <small className="text-muted d-block p-1">
                                    Nenhuma redação com esse tema.
                                  </small>
                                )}
                              </div>
                            </div>
                          )}
                          {/* Adequação ao Tema */}
                          <Form.Group className="mb-3">
                            <Form.Label className="font-weight-bold">
                              <i className="fas fa-bullseye text-primary mr-1"></i>
                              Adequação ao Tema
                              <small className={adequacaoTema === null ? "text-danger font-weight-bold ml-2" : "text-muted ml-2"}>Pontuação: {rotuloNota(adequacaoTema)}</small>
                            </Form.Label>
                            <Form.Control
                              as="select"
                              size="sm"
                              className="mb-2"
                              value={valorSelect(adequacaoTema)}
                              isInvalid={adequacaoTema === null}
                              onChange={(e) => setAdequacaoTema(numeroOuNulo(e.target.value))}
                            >
                              <option value="">Selecione a pontuação...</option>
                              {SCORE_OPTIONS.map(opt => (
                                <option key={`at-opt-${opt}`} value={opt}>{opt.replace('.', ',')}</option>
                              ))}
                            </Form.Control>
                            <div className="border rounded p-2">
                              {SUBCOMPETENCIAS.adequacao_tema.map(item => (
                                <Form.Check
                                  key={item.code}
                                  type="checkbox"
                                  id={`sub-at-${item.code}`}
                                  label={item.label}
                                  checked={subAdequacaoTema.includes(item.code)}
                                  onChange={(e) => {
                                    const checked = e.target.checked;
                                    setSubAdequacaoTema(prev => checked ? [...prev, item.code] : prev.filter(c => c !== item.code));
                                  }}
                                  className="mb-1"
                                />
                              ))}
                            </div>
                          </Form.Group>

                          {/* Tipo de Texto */}
                          <Form.Group className="mb-3">
                            <Form.Label className="font-weight-bold">
                              <i className="fas fa-file-alt text-success mr-1"></i>
                              Tipo de Texto
                              <small className={tipoTexto === null ? "text-danger font-weight-bold ml-2" : "text-muted ml-2"}>Pontuação: {rotuloNota(tipoTexto)}</small>
                            </Form.Label>
                            <Form.Control
                              as="select"
                              size="sm"
                              className="mb-2"
                              value={valorSelect(tipoTexto)}
                              isInvalid={tipoTexto === null}
                              onChange={(e) => setTipoTexto(numeroOuNulo(e.target.value))}
                            >
                              <option value="">Selecione a pontuação...</option>
                              {SCORE_OPTIONS.map(opt => (
                                <option key={`tt-opt-${opt}`} value={opt}>{opt.replace('.', ',')}</option>
                              ))}
                            </Form.Control>
                            <div className="border rounded p-2">
                              {SUBCOMPETENCIAS.tipo_texto.map(item => (
                                <Form.Check
                                  key={item.code}
                                  type="checkbox"
                                  id={`sub-tt-${item.code}`}
                                  label={item.label}
                                  checked={subTipoTexto.includes(item.code)}
                                  onChange={(e) => {
                                    const checked = e.target.checked;
                                    setSubTipoTexto(prev => checked ? [...prev, item.code] : prev.filter(c => c !== item.code));
                                  }}
                                  className="mb-1"
                                />
                              ))}
                            </div>
                          </Form.Group>

                          {/* Argumentação */}
                          <Form.Group className="mb-3">
                            <Form.Label className="font-weight-bold">
                              <i className="fas fa-comments text-warning mr-1"></i>
                              Argumentação
                              <small className={argumentacao === null ? "text-danger font-weight-bold ml-2" : "text-muted ml-2"}>Pontuação: {rotuloNota(argumentacao)}</small>
                            </Form.Label>
                            <Form.Control
                              as="select"
                              size="sm"
                              className="mb-2"
                              value={valorSelect(argumentacao)}
                              isInvalid={argumentacao === null}
                              onChange={(e) => setArgumentacao(numeroOuNulo(e.target.value))}
                            >
                              <option value="">Selecione a pontuação...</option>
                              {SCORE_OPTIONS.map(opt => (
                                <option key={`ar-opt-${opt}`} value={opt}>{opt.replace('.', ',')}</option>
                              ))}
                            </Form.Control>
                            <div className="border rounded p-2">
                              {SUBCOMPETENCIAS.argumentacao.map(item => (
                                <Form.Check
                                  key={item.code}
                                  type="checkbox"
                                  id={`sub-da-${item.code}`}
                                  label={item.label}
                                  checked={subArgumentacao.includes(item.code)}
                                  onChange={(e) => {
                                    const checked = e.target.checked;
                                    setSubArgumentacao(prev => checked ? [...prev, item.code] : prev.filter(c => c !== item.code));
                                  }}
                                  className="mb-1"
                                />
                              ))}
                            </div>
                          </Form.Group>

                          {/* Período e Coesão */}
                          <Form.Group className="mb-3">
                            <Form.Label className="font-weight-bold">
                              <i className="fas fa-link text-info mr-1"></i>
                              Período e Coesão
                              <small className={periodoCoesao === null ? "text-danger font-weight-bold ml-2" : "text-muted ml-2"}>Pontuação: {rotuloNota(periodoCoesao)}</small>
                            </Form.Label>
                            <Form.Control
                              as="select"
                              size="sm"
                              className="mb-2"
                              value={valorSelect(periodoCoesao)}
                              isInvalid={periodoCoesao === null}
                              onChange={(e) => setPeriodoCoesao(numeroOuNulo(e.target.value))}
                            >
                              <option value="">Selecione a pontuação...</option>
                              {SCORE_OPTIONS.map(opt => (
                                <option key={`pc-opt-${opt}`} value={opt}>{opt.replace('.', ',')}</option>
                              ))}
                            </Form.Control>
                            <div className="border rounded p-2">
                              {SUBCOMPETENCIAS.periodo_coesao.map(item => (
                                <Form.Check
                                  key={item.code}
                                  type="checkbox"
                                  id={`sub-ec-${item.code}`}
                                  label={item.label}
                                  checked={subPeriodoCoesao.includes(item.code)}
                                  onChange={(e) => {
                                    const checked = e.target.checked;
                                    setSubPeriodoCoesao(prev => checked ? [...prev, item.code] : prev.filter(c => c !== item.code));
                                  }}
                                  className="mb-1"
                                />
                              ))}
                            </div>
                          </Form.Group>

                          {/* Modalidade */}
                          <Form.Group className="mb-4">
                            <Form.Label className="font-weight-bold">
                              <i className="fas fa-spell-check text-danger mr-1"></i>
                              Modalidade
                              <small className={modalidade === null ? "text-danger font-weight-bold ml-2" : "text-muted ml-2"}>Pontuação: {rotuloNota(modalidade)}</small>
                            </Form.Label>
                            <Form.Control
                              as="select"
                              size="sm"
                              className="mb-2"
                              value={valorSelect(modalidade)}
                              isInvalid={modalidade === null}
                              onChange={(e) => setModalidade(numeroOuNulo(e.target.value))}
                            >
                              <option value="">Selecione a pontuação...</option>
                              {SCORE_OPTIONS.map(opt => (
                                <option key={`mo-opt-${opt}`} value={opt}>{opt.replace('.', ',')}</option>
                              ))}
                            </Form.Control>
                            <div className="border rounded p-2">
                              {SUBCOMPETENCIAS.modalidade.map(item => (
                                <Form.Check
                                  key={item.code}
                                  type="checkbox"
                                  id={`sub-mo-${item.code}`}
                                  label={item.label}
                                  checked={subModalidade.includes(item.code)}
                                  onChange={(e) => {
                                    const checked = e.target.checked;
                                    setSubModalidade(prev => checked ? [...prev, item.code] : prev.filter(c => c !== item.code));
                                  }}
                                  className="mb-1"
                                />
                              ))}
                            </div>
                          </Form.Group>

                          {/* Nota Total */}
                          <div className={`alert mb-4 ${competenciasFaltando > 0 ? 'alert-warning' : 'alert-success'}`}>
                            <h6 className="mb-1">
                              <i className="fas fa-trophy mr-1"></i>
                              Nota Total
                            </h6>

                            <h3 className={`mb-0 ${competenciasFaltando > 0 ? 'text-warning' : 'text-success'}`}>
                              {notaArredondada}/20
                            </h3>

                            {competenciasFaltando > 0 && (
                              <small className="d-block mt-2 text-dark">
                                <i className="fas fa-exclamation-triangle mr-1"></i>
                                {competenciasFaltando === 1
                                  ? 'Falta 1 competência sem pontuação — o total ainda não está completo.'
                                  : `Faltam ${competenciasFaltando} competências sem pontuação — o total ainda não está completo.`}
                              </small>
                            )}
                          </div>

                          {/* Resumo de Marcações */}
                          <div className="marcacoes-container mb-4 p-3 bg-white rounded border">
                            <h6 className="mb-2">
                              <i className="fas fa-thumbtack text-secondary mr-1"></i>
                              Marcações
                            </h6>
                            {marcacoes.length > 0 ? (
                              <div>
                                {marcacoes.map((marcacao, index) => (
                                  <div key={index} className="mb-2 p-2 border rounded marcacao-item" style={{ backgroundColor: hexToRgba(getCompetenciaColor(resolveCompetencia(marcacao)), 0.15), borderColor: hexToRgba(getCompetenciaColor(resolveCompetencia(marcacao)), 0.35) }}>
                                    <div className="d-flex align-items-center mb-1">
                                      <span className="badge mr-2" style={{ backgroundColor: getCompetenciaColor(resolveCompetencia(marcacao)), color: '#fff' }}>
                                      </span>
                                      <small className="text-muted">
                                        {getCompetenciaLabel(resolveCompetencia(marcacao))}
                                      </small>
                                    </div>
                                    {(marcacao.content?.image || marcacao.image || marcacao.image_url) && (
                                      <div className="mb-2">
                                        <img 
                                          src={marcacao.content?.image || marcacao.image || marcacao.image_url} 
                                          alt="Marcação" 
                                          className="marcacao-image"
                                          style={{ 
                                            maxWidth: '100%', 
                                            height: 'auto', 
                                            maxHeight: '100px',
                                            border: '1px solid #ddd',
                                            borderRadius: '4px'
                                          }}
                                          onError={(e) => {
                                            console.error('Erro ao carregar imagem:', e);
                                            console.log('URL da imagem:', e.target.src);
                                          }}
                                        />
                                      </div>
                                    )}
                                    {(marcacao.content?.text || marcacao.comment) && (
                                      <small className="text-dark d-block">
                                        {marcacao.content?.text || marcacao.comment}
                                      </small>
                                    )}
                                    {!marcacao.saved && (
                                      <small className="text-warning d-block">
                                        <i className="fas fa-exclamation-circle mr-1"></i>
                                        Marcação não salva
                                      </small>
                                    )}
                                    {marcacao.deleted && (
                                      <div className="d-flex align-items-center justify-content-between mt-2">
                                        <small className="text-danger">
                                          <i className="fas fa-trash mr-1"></i>
                                          Marcada para exclusão (pendente salvar)
                                        </small>
                                        <button
                                          className="btn btn-sm btn-outline-danger"
                                          onClick={() => {
                                            try {
                                              pdfRef.current?.cancelHighlightDeletion?.(marcacao.id);
                                              setMarcacoes(prev => prev.map(m => m.id === marcacao.id ? { ...m, deleted: false } : m));
                                              toast.success('Exclusão cancelada para a marcação.');
                                            } catch (e) {
                                              console.error('Erro ao cancelar exclusão:', e);
                                              toast.error('Erro ao cancelar exclusão');
                                            }
                                          }}
                                        >
                                          Cancelar exclusão
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <small className="text-muted">Nenhuma marcação</small>
                            )}
                          </div>

                          {/* Ações movidas para o topo do card */}
                        </div>
                      </div>
                  </Col>

                  {/* Visualização da Redação (lado direito) */}
                  <Col xs={12} md={10} lg={10}>
                    <div className="card editor-card border-0 shadow-sm">
                      <div className="card-body p-0">
                        {console.log("🔍 Verificando URL da redação:", { 
                          url: redacao.url,
                          redacao: redacao 
                        })}
                        {redacao.url ? (
                          <div className="pdf-container">
                            <CarregarPDFUERJ
                              /*
                                `key` força remontar ao trocar de redação pelo seletor
                                lateral: o componente guarda as marcações no próprio
                                estado interno e, sem isso, as da redação anterior
                                apareceriam sobre o PDF da nova.
                              */
                              key={id}
                              ref={pdfRef}
                              redacaoId={id}
                              pdfUrl={`https://redacoes.nyc3.digitaloceanspaces.com/${redacao.url}`}
                              competenciaAtiva={competenciaAtiva}
                              onCompetenciaChange={setCompetenciaAtiva}
                              onHighlightAdd={(highlight) => {
                                console.log("Nova marcação adicionada:", highlight);
                                setMarcacoes(prev => [...prev, highlight]);
                              }}
                              onHighlightUpdate={(highlightId, position, content) => {
                                console.log("Marcação atualizada:", highlightId);
                              }}
                              onHighlightMarkDelete={(highlightId) => {
                                console.log('Marcação marcada para exclusão:', highlightId);
                                setMarcacoes(prev => prev.map(m => m.id === highlightId ? { ...m, deleted: true } : m));
                              }}
                              onHighlightCancelDelete={(highlightId) => {
                                console.log('Cancelado marcar para exclusão:', highlightId);
                                setMarcacoes(prev => prev.map(m => m.id === highlightId ? { ...m, deleted: false } : m));
                              }}
                              onHighlightDelete={(highlightId) => {
                                console.log("Marcação removida:", highlightId);
                                setMarcacoes(prev => prev.filter(m => m.id !== highlightId));
                              }}
                              onHighlightSave={(savedHighlightIds) => {
                                console.log("Marcações salvas:", savedHighlightIds);
                                setMarcacoes(prev => prev.map(m => 
                                  savedHighlightIds.includes(m.id) ? { ...m, saved: true } : m
                                ));
                              }}
                            />
                          </div>
                        ) : (
                          <div className="text-center p-5">
                            <i className="fas fa-file-pdf text-muted" style={{fontSize: '3rem'}}></i>
                            <p className="mt-3 text-muted">Arquivo da redação não disponível</p>
                            <small className="text-muted">
                              URL: {redacao.url || 'Não definida'}
                            </small>
                          </div>
                        )}
                      </div>
                    </div>
                  </Col>
                </Row>
              </S.MainContent>
            </Col>
          </Row>
        </Container>
      </S.MainWrapper>

      {/*
        Confirmação antes de gravar na correção de outro corretor. Modal e não
        toast: o toast some sozinho, e aqui a ideia é parar quem está prestes a
        assumir uma correção que não começou — no UERJ isso não tem volta.
      */}
      <Modal
        show={confirmandoEdicao}
        onHide={() => !saving && setConfirmandoEdicao(false)}
        centered
      >
        <Modal.Header closeButton className="bg-warning">
          <Modal.Title style={{ fontSize: "1.1rem" }}>
            <i className="fas fa-triangle-exclamation mr-2" />
            Esta correção é de outro corretor
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <p className="mb-2">
            A correção desta redação foi {dono?.finalizada ? "finalizada" : "iniciada"} por{" "}
            <strong>{dono?.nome}</strong>.
          </p>
          <p className="mb-0">
            Ao finalizar, você fica registrado como <strong>revisor</strong> — o crédito
            da correção continua sendo dele.
          </p>
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="light"
            onClick={() => setConfirmandoEdicao(false)}
            disabled={saving}
          >
            Cancelar
          </Button>
          <Button
            variant="danger"
            disabled={saving}
            onClick={async () => {
              await salvarCorrecao();
              setConfirmandoEdicao(false);
            }}
          >
            {saving ? "Finalizando…" : "Confirmar e finalizar"}
          </Button>
        </Modal.Footer>
      </Modal>
    </Template>
  );
}

export default CorrecaoUERJ;